// calendarUtils.js - Utilidades para agregar eventos al calendario

/**
 * Genera un archivo .ics válido para calendarios
 * @param {Object} event - Detalles del evento
 * @returns {string} Contenido del archivo .ics
 */
export const generateICS = (event) => {
  const formatDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };

  const startDate = new Date(event.startDate);
  const endDate = new Date(event.endDate);

  // Generar un UID único para el evento
  const uid = `cec2026-${Date.now()}@cecguatemala.org`;
  
  // Formatear la descripción con saltos de línea
  const description = event.description.replace(/\n/g, '\\n');
  
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//CEC//Congreso Espacial Centroamericano//ES',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VTIMEZONE',
    'TZID:America/Guatemala',
    'BEGIN:STANDARD',
    'DTSTART:19700101T000000',
    'TZOFFSETFROM:-0600',
    'TZOFFSETTO:-0600',
    'END:STANDARD',
    'END:VTIMEZONE',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatDate(new Date())}`,
    `DTSTART;TZID=America/Guatemala:${formatDate(startDate)}`,
    `DTEND;TZID=America/Guatemala:${formatDate(endDate)}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${event.location}`,
    `URL:${event.url || 'https://cecguatemala.org'}`,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'BEGIN:VALARM',
    'TRIGGER:-P1W',
    'ACTION:DISPLAY',
    'DESCRIPTION:Recordatorio: CEC 2026 en una semana',
    'END:VALARM',
    'BEGIN:VALARM',
    'TRIGGER:-P1D',
    'ACTION:DISPLAY',
    'DESCRIPTION:Recordatorio: CEC 2026 mañana',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  return icsContent;
};

/**
 * Descarga un archivo .ics
 * @param {string} content - Contenido del archivo
 * @param {string} filename - Nombre del archivo
 */
export const downloadICSFile = (content, filename = 'evento.ics') => {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Genera URLs para diferentes servicios de calendario
 * @param {Object} event - Detalles del evento
 * @returns {Object} URLs para cada servicio
 */
export const generateCalendarLinks = (event) => {
  const startDate = new Date(event.startDate);
  const endDate = new Date(event.endDate);
  
  // Formato para Google Calendar (YYYYMMDDTHHmmss)
  const formatGoogleDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '').replace('Z', '');
  };

  // Formato para Outlook/Office 365
  const formatOutlookDate = (date) => {
    return date.toISOString();
  };

  const googleStart = formatGoogleDate(startDate);
  const googleEnd = formatGoogleDate(endDate);
  
  const links = {
    // Google Calendar
    google: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${googleStart}/${googleEnd}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}&ctz=America/Guatemala`,
    
    // Outlook Web
    outlook: `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(event.title)}&startdt=${formatOutlookDate(startDate)}&enddt=${formatOutlookDate(endDate)}&body=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`,
    
    // Office 365
    office365: `https://outlook.office.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(event.title)}&startdt=${formatOutlookDate(startDate)}&enddt=${formatOutlookDate(endDate)}&body=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`,
    
    // Yahoo Calendar
    yahoo: `https://calendar.yahoo.com/?v=60&title=${encodeURIComponent(event.title)}&st=${formatGoogleDate(startDate)}&et=${formatGoogleDate(endDate)}&desc=${encodeURIComponent(event.description)}&in_loc=${encodeURIComponent(event.location)}`
  };

  return links;
};

/**
 * Detecta el tipo de dispositivo y navegador
 * @returns {Object} Información del dispositivo
 */
export const detectDevice = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  return {
    isMobile: /iphone|ipad|ipod|android|webos|blackberry|windows phone/i.test(userAgent),
    isIOS: /iphone|ipad|ipod/i.test(userAgent),
    isAndroid: /android/i.test(userAgent),
    isMac: /macintosh|mac os x/i.test(userAgent),
    isWindows: /windows|win32/i.test(userAgent),
    isOutlookUser: /outlook|office/i.test(userAgent)
  };
};

/**
 * Datos del evento CEC 2026
 */
export const CEC_2026_EVENT = {
  title: 'III Congreso Espacial Centroamericano - CEC 2026',
  startDate: '2026-09-16T08:30:00-06:00',
  endDate: '2026-09-18T17:30:00-06:00',
  location: 'Universidad del Valle de Guatemala (UVG), 18 Avenida 11-95 zona 15, Vista Hermosa III, Ciudad de Guatemala, Guatemala',
  description: `III Congreso Espacial Centroamericano
  
El evento más importante del sector espacial en Centroamérica.

📅 Fecha: 16 al 18 de septiembre de 2026
📍 Lugar: Universidad del Valle de Guatemala (UVG)
🌎 Ciudad de Guatemala, Guatemala

Agenda:
• Conferencias magistrales
• Paneles de expertos internacionales
• Networking con profesionales del sector

Más información en: https://cecspace.org/`,
  url: 'https://cecspace.org/'
};